import { useEmailAuth } from '@/hooks/use-email-auth';
import { useProviderAuth } from '@/hooks/use-provider-auth';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Alert,
  Anchor,
  Box,
  BoxProps,
  Checkbox,
  Divider,
  Group,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
  PasswordInput,
} from '@mantine/core';
import { joiResolver, useForm } from '@mantine/form';
import { UserCredential } from 'firebase/auth';
import Joi from 'joi';
import React, { useEffect, useState, useRef } from 'react';
import GoogleSVG from '../../../public/img/icons/social/google.svg';
import Button from '../Button';
import FAIcon from '../FAIcon';

const Root = styled(Box, {
  shouldForwardProp: (prop: PropertyKey) => ![''].includes(prop.toString()),
})<Partial<LoginFormProps>>((props) => css``);

interface Props {
  onLogin: (creds: UserCredential) => void;
  onPasswordResetRequest?: () => void;
  onSignupRequest?: () => void;
  trustDevice?: boolean;
  onTrustDeviceChange?: (value: boolean) => void;
  children?: React.ReactNode;
}

export type LoginFormProps = Props & BoxProps;

const LoginForm: React.FC<LoginFormProps> = ({
  onLogin,
  onPasswordResetRequest,
  onSignupRequest,
  trustDevice = false,
  onTrustDeviceChange,
  ...props
}) => {
  const [googleAuth, googleAuthResponse] = useProviderAuth('google');
  const [login, loginResponse] = useEmailAuth();
  const [isHandlingAuth, setIsHandlingAuth] = useState<boolean>(false);
  const [showError, setShowError] = useState(true);
  const [showPassword, setShowPassword] = useState(true);
  const formRef = useRef<HTMLFormElement>(null);

  const schema = Joi.object({
    email: Joi.string()
      .email({ tlds: { allow: false } })
      .required()
      .messages({
        'string.empty': 'Please enter an e-mail address',
        'string.email': 'Please enter a valid e-mail address',
      }),
    password: Joi.string().required().messages({
      'string.empty': 'Please enter a password',
    }),
  });

  const loginForm = useForm({
    initialValues: {
      email: '',
      password: '',
    },
    validate: joiResolver(schema),
  });

  // Handle autofill detection
  useEffect(() => {
    const checkAutofill = () => {
      const form = formRef.current;
      if (form) {
        const emailInput = form.querySelector('input[type="email"]') as HTMLInputElement;
        const passwordInput = form.querySelector('input[type="password"]') as HTMLInputElement;
        
        if (emailInput?.value) {
          loginForm.setFieldValue('email', emailInput.value);
        }
        if (passwordInput?.value) {
          loginForm.setFieldValue('password', passwordInput.value);
        }
      }
    };

    // Check immediately and after a short delay to catch browser autofill
    checkAutofill();
    setTimeout(checkAutofill, 100);
    setTimeout(checkAutofill, 500);
    setTimeout(checkAutofill, 1000);

    // Add mutation observer to detect changes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          checkAutofill();
        }
      });
    });

    const form = formRef.current;
    if (form) {
      observer.observe(form, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const pending =
    googleAuthResponse.status === 'pending' ||
    loginResponse.status === 'pending' ||
    isHandlingAuth;

  useEffect(() => {
    if (!isHandlingAuth && loginResponse.status === 'complete' && loginResponse.info) {
      setIsHandlingAuth(true);
      const userCred = loginResponse.info as UserCredential;
      
      if (trustDevice && userCred.user) {
        localStorage.setItem(`trust_preference_${userCred.user.uid}`, 'true');
      }
      
      onLogin(userCred);
    }
  }, [loginResponse, onLogin, isHandlingAuth, trustDevice]);

  const handleForgotPassword = (e: React.MouseEvent) => {
    e.preventDefault();
    if (onPasswordResetRequest) {
      onPasswordResetRequest();
    }
  };

  return (
    <Root {...props} p={30}>
      <Title size={32}>Login</Title>

      <Text opacity={0.7} fz={14} mt={5}>
        Please enter your details to continue
      </Text>

      <Divider my={20} />

      <form
        ref={formRef}
        onSubmit={loginForm.onSubmit(async ({ email, password }) =>
          login(email, password)
        )}
        autoComplete="on"
        name="login"
      >
        <Stack gap="md">
          <TextInput
            label={<strong>Email</strong>}
            placeholder="E-mail address"
            type="email"
            name="email"
            id="email"
            autoComplete="email"
            disabled={pending}
            leftSection={<FAIcon name="envelope" size={14} />}
            {...loginForm.getInputProps('email')}
          />

          <PasswordInput
            label={<strong>Password</strong>}
            placeholder="Enter your password"
            name="password"
            id="password"
            autoComplete="current-password"
            disabled={pending}
            defaultVisible={true}
            visible={showPassword}
            onVisibilityChange={setShowPassword}
            leftSection={<FAIcon name="lock" size={14} />}
            {...loginForm.getInputProps('password')}
          />

          <Group justify="space-between" align="center">
            <Checkbox
              label={
                <Text size="sm">
                  Trust this device
                  <Text size="xs" c="dimmed" mt={2}>
                    Skip verification next time
                  </Text>
                </Text>
              }
              checked={trustDevice}
              onChange={(event) => onTrustDeviceChange?.(event.currentTarget.checked)}
            />
            <Anchor
              size="sm"
              c="dark"
              fw={500}
              onClick={handleForgotPassword}
              style={{ cursor: 'pointer' }}
            >
              Forgot password?
            </Anchor>
          </Group>

          {loginResponse.status === 'error' && (
            <Alert
              color="red"
              title="Login Error"
              icon={<FAIcon name="exclamation-circle" size={16} />}
              styles={{
                root: {
                  backgroundColor: '#FFF5F5',
                  border: '1px solid #FED7D7'
                },
                title: {
                  color: '#C53030'
                },
                message: {
                  color: '#E53E3E'
                }
              }}
            >
              {(
                {
                  'auth/invalid-credential': 'Incorrect email or password. Please try again or reset your password.',
                  'auth/wrong-password': 'Incorrect email or password. Please try again or reset your password.',
                  'auth/user-not-found': 'No account found with this email. Please check your email or sign up.',
                  'auth/too-many-requests': 'Too many failed attempts. Please try again later or reset your password.',
                } as any
              )[loginResponse.info.cause] ?? loginResponse.info.message}
            </Alert>
          )}

          <Button
            component="button"
            fullwidth
            transition={false}
            color="dark"
            type="submit"
            loading={pending}
            disabled={pending}
            leftSection={<FAIcon name="sign-in-alt" size={14} />}
          >
            Sign in
          </Button>
        </Stack>
      </form>

      <Divider mt={30} mb={15} label={<Text>or</Text>} />

      <SimpleGrid cols={1} spacing={10}>
        <Button
          component="button"
          color="default"
          fullwidth
          radius="sm"
          transition={false}
          disabled={pending}
          onClick={googleAuth}
          leftSection={
            <Box w={16} style={{ display: 'flex', alignContent: 'center' }}>
              <GoogleSVG />
            </Box>
          }
        >
          <Text fz={14} fw={500}>
            Sign in with Google
          </Text>
        </Button>
      </SimpleGrid>

      <Text size="sm" mt={30} ta="center">
        Don&lsquo;t have an account? &nbsp;
        <Anchor
          c="dark"
          fw={500}
          onClick={onSignupRequest}
          style={{ cursor: 'pointer' }}
        >
          Sign up
        </Anchor>
      </Text>

      <Group mt={30} justify="center">
        <Anchor
          href="https://www.iubenda.com/terms-and-conditions/12581878"
          target="_blank"
          c="gray.6"
          size="11px"
        >
          Terms and Conditions
        </Anchor>

        <Anchor
          href="https://www.iubenda.com/privacy-policy/12581878"
          target="_blank"
          c="gray.6"
          size="11px"
        >
          Privacy Policy
        </Anchor>
      </Group>
    </Root>
  );
};

export default LoginForm;